import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Star,
  Send,
  CheckCircle,
  MessageSquare,
  Heart,
  ThumbsUp,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_4b79ec74-cc21-403d-a390-656b8c7298f7/artifacts/m95yszhz_1.png";

export default function FeedbackPage() {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    feedback_type: 'general',
    service_received: '',
    therapist_name: '',
    rating: 0,
    message: '',
    would_recommend: ''
  });

  const feedbackTypes = [
    { value: 'general', label: 'General Feedback' },
    { value: 'service', label: 'Service Feedback' },
    { value: 'therapist', label: 'Therapist Feedback' },
    { value: 'facilities', label: 'Facilities Feedback' },
    { value: 'complaint', label: 'Complaint' },
    { value: 'suggestion', label: 'Suggestion' },
    { value: 'compliment', label: 'Compliment' }
  ];

  const services = [
    'Occupational Therapy',
    'Physiotherapy',
    'Speech & Language Therapy',
    'Psychology',
    'Rehabilitation',
    'Assessment',
    'Other'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (rating === 0) {
      toast.error('Please provide a rating');
      return;
    }

    if (!form.message.trim()) {
      toast.error('Please provide your feedback message');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/feedback/submit`, {
        ...form,
        rating
      });

      if (response.data.success) {
        setSubmitted(true);
        toast.success('Thank you for your feedback!');
      }
    } catch (error) {
      toast.error('Failed to submit feedback. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-8 pb-8">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-[#1e3a5f] mb-2">Thank You!</h1>
            <p className="text-slate-600 mb-6">
              Your feedback has been submitted successfully. We truly appreciate you taking the time to share your thoughts with us.
            </p>
            <div className="flex items-center justify-center gap-2 text-[#2a9d8f] mb-6">
              <Heart className="w-5 h-5 fill-current" />
              <span className="font-medium">ICRCL Team</span>
            </div>
            <Button 
              onClick={() => {
                setSubmitted(false);
                setRating(0);
                setForm({
                  name: '',
                  email: '',
                  phone: '',
                  feedback_type: 'general',
                  service_received: '',
                  therapist_name: '',
                  rating: 0,
                  message: '',
                  would_recommend: ''
                });
              }}
              variant="outline"
            >
              Submit Another Feedback
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="ICRCL Logo" className="w-20 h-20 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-[#1e3a5f]">
            Innovative Care and Rehab Centre
          </h1>
          <p className="text-slate-600 mt-2">We value your feedback</p>
        </div>

        <Card className="shadow-lg">
          <CardHeader className="bg-gradient-to-r from-[#1e3a5f] to-[#2a5a8f] text-white rounded-t-lg">
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Share Your Experience
            </CardTitle>
            <CardDescription className="text-blue-100">
              Your feedback helps us improve our services
            </CardDescription>
          </CardHeader>

          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Rating */}
              <div className="text-center">
                <Label className="block mb-3 text-lg font-medium text-[#1e3a5f]">
                  How would you rate your experience?
                </Label>
                <div className="flex justify-center gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setRating(star)}
                      onMouseEnter={() => setHoverRating(star)}
                      onMouseLeave={() => setHoverRating(0)}
                      className="p-1 transition-transform hover:scale-110"
                    >
                      <Star
                        className={`w-10 h-10 ${
                          star <= (hoverRating || rating)
                            ? 'fill-yellow-400 text-yellow-400'
                            : 'text-slate-300'
                        }`}
                      />
                    </button>
                  ))}
                </div>
                <p className="text-sm text-slate-500 mt-2">
                  {rating === 0 && 'Click to rate'}
                  {rating === 1 && 'Poor'}
                  {rating === 2 && 'Fair'}
                  {rating === 3 && 'Good'}
                  {rating === 4 && 'Very Good'}
                  {rating === 5 && 'Excellent'}
                </p>
              </div>

              {/* Contact Info */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Your Name</Label>
                  <Input
                    id="name"
                    placeholder="John Smith"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email (Optional)</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="john@example.com"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Phone (Optional)</Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+44 7..."
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
              </div>

              {/* Feedback Type */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Feedback Type</Label>
                  <Select 
                    value={form.feedback_type} 
                    onValueChange={(v) => setForm({ ...form, feedback_type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {feedbackTypes.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Service Received</Label>
                  <Select 
                    value={form.service_received} 
                    onValueChange={(v) => setForm({ ...form, service_received: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select service" />
                    </SelectTrigger>
                    <SelectContent>
                      {services.map(service => (
                        <SelectItem key={service} value={service}>
                          {service}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Therapist Name (Optional) */}
              {(form.feedback_type === 'therapist' || form.feedback_type === 'compliment') && (
                <div className="space-y-2">
                  <Label htmlFor="therapist">Therapist Name (Optional)</Label>
                  <Input
                    id="therapist"
                    placeholder="Name of the therapist"
                    value={form.therapist_name}
                    onChange={(e) => setForm({ ...form, therapist_name: e.target.value })}
                  />
                </div>
              )}

              {/* Message */}
              <div className="space-y-2">
                <Label htmlFor="message">Your Feedback *</Label>
                <Textarea
                  id="message"
                  placeholder="Please share your experience with us..."
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  required
                />
              </div>

              {/* Would Recommend */}
              <div className="space-y-2">
                <Label>Would you recommend us to others?</Label>
                <div className="flex gap-4">
                  {['Yes, definitely', 'Probably', 'Not sure', 'Probably not', 'No'].map(option => (
                    <label key={option} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="recommend"
                        value={option}
                        checked={form.would_recommend === option}
                        onChange={(e) => setForm({ ...form, would_recommend: e.target.value })}
                        className="text-[#2a9d8f] focus:ring-[#2a9d8f]"
                      />
                      <span className="text-sm">{option}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Submit Button */}
              <Button 
                type="submit" 
                className="w-full bg-[#2a9d8f] hover:bg-[#238b7e] text-lg py-6"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Submit Feedback
                  </>
                )}
              </Button>

              {/* Privacy Note */}
              <p className="text-xs text-slate-500 text-center">
                Your feedback is confidential and will be used to improve our services.
                By submitting, you agree to our privacy policy.
              </p>
            </form>
          </CardContent>
        </Card>

        {/* Contact Info */}
        <div className="text-center mt-8 text-slate-600">
          <p className="text-sm">
            Need to speak with us directly?
          </p>
          <p className="text-sm">
            Email: <a href="mailto:admin@icrcl.co.uk" className="text-[#2a9d8f] hover:underline">admin@icrcl.co.uk</a>
          </p>
          <p className="text-sm">
            Website: <a href="https://www.icrcl.co.uk" target="_blank" rel="noopener noreferrer" className="text-[#2a9d8f] hover:underline">www.icrcl.co.uk</a>
          </p>
        </div>
      </div>
    </div>
  );
}
